import { useCallback, useEffect, useState } from "react";
import { fetchAxios } from "../API/fetchAxios";
import { getIbAccountQueryParams } from "../Constants/accountTypes";
import { toTransferBalanceView } from "./Normalizers";

const EMPTY_VIEW = {
  availableCents: null,
  spendableCents: null,
  limitCents: 0,
  allowOverdraft: false,
  showLimit: false,
  formattedAvailable: "—",
  formattedSpendable: "—",
  formattedLimit: "—",
  formattedRemainingLimit: "—",
  formattedDisplay: "—",
};

/**
 * Balance for the selected IB account via MobileService `/v1/ib/accounts/balances`
 * → BaaS `GET /v1/accounts/{activeId}/balances` (primary BRL cents).
 *
 * `balance` = Available (unallocated). Gate transfers against `spendable`.
 * `formattedBalance` = spendable when Over Balance is on, else Available.
 */
export const useTransferAccountBalance = (accountId) => {
  const [view, setView] = useState(EMPTY_VIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!accountId) return;

    setLoading(true);
    setError(false);

    try {
      const response = await fetchAxios({
        verb: "GET",
        context: "accountBalance",
        responseType: "",
        queryParameters: getIbAccountQueryParams(accountId),
        cancelToken: undefined,
      });

      setView(toTransferBalanceView(response?.data ?? response));
      setError(false);
    } catch {
      setView(EMPTY_VIEW);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  return {
    balance: view.availableCents,
    spendable: view.spendableCents,
    availableCents: view.availableCents,
    spendableCents: view.spendableCents,
    limitCents: view.limitCents,
    allowOverdraft: view.allowOverdraft,
    showLimit: view.showLimit,
    formattedAvailable: view.formattedAvailable,
    formattedSpendable: view.formattedSpendable,
    formattedLimit: view.formattedLimit,
    formattedRemainingLimit: view.formattedRemainingLimit,
    formattedBalance: view.formattedDisplay,
    loading,
    error,
    refresh: fetchBalance,
  };
};
